import { ChangeDetectorRef, Component, forwardRef, Injector, Input, OnDestroy, OnInit, input } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NgControl, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { fadeInOut, fadeInOutHidden } from 'src/app/constants/animations';
import { LookupsService } from 'src/app/services/lookups/lookups.service';
import { BackEndDateFormat } from 'src/app/constants/strings';
import dayjs from 'dayjs';
import toArray from 'dayjs/plugin/toArray';
import { ILookupList } from 'src/app/interfaces/ILookupList.interface';
import { IErrorMessage } from 'src/app/interfaces/appAbstractions/IErrorMessage.interface';
import { debounceTime } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { TooltipComponent } from '../../tooltip/tooltip.component';

@Component({
    selector: 'app-question-date-expiry',
    templateUrl: './question-date-expiry.component.html',
    styleUrls: ['./question-date-expiry.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => QuestionDateExpiryComponent),
        }
    ],
    animations: [
        fadeInOut,
        fadeInOutHidden
    ],
    imports: [FormsModule, ReactiveFormsModule, NgClass, TooltipComponent]
})
export class QuestionDateExpiryComponent implements OnInit, OnDestroy, ControlValueAccessor {

    public dateForm: UntypedFormGroup;
    public disabled: any = false;

    private ngControl: NgControl | undefined;

    readonly questionText = input<string>('');
    @Input() toolTipNames: string;
    readonly name = input<string>('Date');
    readonly errorText = input<string>('');
    readonly optionsList = input<ILookupList | undefined>(undefined);
    readonly errorMessages = input<IErrorMessage[]>([]);
    readonly addHotJarSuppressionTag = input<boolean>(false);

    private touched: boolean = false;

    private onChange = (_: any) => { };
    private onTouch = (_: any) => { };

    private dateFormChangeEventSubscription: any;

    constructor(private formBuilder: UntypedFormBuilder,
        public lookupService: LookupsService,
        private injector: Injector,
        private cdRef: ChangeDetectorRef) {
        this.dateForm = formBuilder.group(
            {
                month: this.formBuilder.control({ value: null, disabled: false }, { validators: [Validators.required], updateOn: 'change' }),
                year: this.formBuilder.control({ value: null, disabled: false }, { validators: [Validators.required], updateOn: 'change' }),
            }
        )
        dayjs.extend(toArray);
    }

    ngOnInit(): void {
        this.setUpOnChangeListeners();
        this.ngControl = this.injector.get(NgControl);
    }

    ngOnDestroy(): void {
        this.unSubscribeEventListeners();
    }

    writeValue(obj: any): void {

        if (obj) {
            const date = dayjs(obj, BackEndDateFormat, true);
            if (date.isValid()) {
                const datePartsArray = date.toArray();
                this.dateForm.controls['year'].setValue(datePartsArray[0].toString());
                this.dateForm.controls['month'].setValue(this.pad((datePartsArray[1]), 2)); // zero based month padded with leading zero
            }
        } else {
            this.dateForm.controls['year'].setValue(null);
            this.dateForm.controls['month'].setValue(null);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn
    }

    public setDisabledState(val: any) {
        this.disabled = val;
        val ? this.dateForm.disable() : this.dateForm.enable();
    }

    public onDayChange(event: any): void {

    }

    public onSelectBlur(): void {

        if (!this.touched) {
            const monthTouched = this.dateForm.controls['month']?.touched
            const yearTouched = this.dateForm.controls['year']?.touched
            if (monthTouched && yearTouched) {
                this.touched = true;
                this.onTouch(this.touched);
            }
        }
    }

    public getErrorMessage(): string {
        let result: string = '';

        if (this.ngControl?.errors) {
            if (this.errorMessages().length > 0) {
                for (const errorMessage of this.errorMessages()) {

                    const controlError = this.ngControl.errors[errorMessage.key]
                    if (controlError) {
                        result = errorMessage.message;
                        break;
                    }
                }
                if (result === '') {
                    result = this.errorText();
                }
            } else {
                result = this.errorText();
            }
        }

        return result
    }

    public shouldShowError(): boolean {
        return this.ngControl.invalid && this.ngControl.dirty && this.ngControl.touched;
    }

    public getIdText(val: string): string {

        return `${this.name()}-date-${val}`;
    }

    private onDateFormChangeEventSubscription(val: any) {
        let result: string | null = null;

        if (this.dateForm.valid) {
            const dateString = `${this.getFormValue('year')}-${this.pad((+this.getFormValue('month') + 1), 2)}-01`;
            const date = dayjs(dateString, BackEndDateFormat, true);
            if (date.isValid()) {
                result = date.endOf('month').format(BackEndDateFormat);
            }
        }

        this.onChange(result);
        this.cdRef.markForCheck();
    }

    private pad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    private setUpOnChangeListeners(): void {
        this.dateFormChangeEventSubscription = this.dateForm?.valueChanges
            .pipe(debounceTime(100))
            .subscribe(val => { this.onDateFormChangeEventSubscription(val) });

    }

    private unSubscribeEventListeners(): void {
        this.dateFormChangeEventSubscription?.unsubscribe();
    }

    private getFormValue(controlName: string): string | null | undefined {
        let result = this.dateForm?.controls[controlName]?.value;
        return result;
    }
}
